import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Handshake, LayoutDashboard, FolderHeart, Users, ArrowLeft, Loader2, Briefcase } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
    { to: '/app/adrns', end: true, icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/app/adrns/dossiers', icon: FolderHeart, label: "Dossiers d'accompagnement" },
    { to: '/app/adrns/missions', icon: Briefcase, label: 'Missions' },
    { to: '/app/adrns/membres', icon: Users, label: 'Membres ADRNS' },
];

export default function AdrnsLayout() {
    const { session } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!session) return;
        const hdrs = { headers: { Authorization: `Bearer ${session.access_token}` } };
        axios.get(`${API}/adrns/me`, hdrs)
            .then(r => setStatus(r.data))
            .catch(() => {
                // erreur transitoire (réseau/timing) : re-tente une fois avant de refuser l'accès
                setTimeout(() => {
                    axios.get(`${API}/adrns/me`, hdrs)
                        .then(r => setStatus(r.data)).catch(() => setStatus({ member: false }));
                }, 900);
            });
    }, [session]);

    if (status === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
    if (!status.member) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="card-soft p-10 max-w-md text-center" data-testid="adrns-access-denied">
                    <Handshake size={32} className="mx-auto mb-4 text-muted-foreground" />
                    <h1 className="font-heading text-2xl mb-2">Espace ADRNS</h1>
                    <p className="text-sm text-foreground/70">Accès réservé aux membres accompagnateurs habilités par le Super Admin.</p>
                    <button onClick={() => navigate('/app/dashboard')} className="mt-6 text-sm text-primary underline">← Retour à mon espace</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-[#0e1b14]" data-testid="adrns-layout">
            <aside className="w-64 shrink-0 flex flex-col border-r border-emerald-900/60">
                <div className="p-5 border-b border-emerald-900/60">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-lg bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center">
                            <Handshake size={17} className="text-emerald-400" />
                        </div>
                        <div>
                            <div className="text-emerald-50 font-heading leading-tight">Espace ADRNS</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-500/80">Accompagnement</div>
                        </div>
                    </div>
                    <div className="mt-3 text-[10px] uppercase tracking-wider text-emerald-400/70">
                        {status.role === 'coordinateur' ? 'Coordinateur · tous les dossiers' : 'Accompagnateur · dossiers assignés'}
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {NAV.map(item => (
                        <NavLink key={item.to} to={item.to} end={item.end}
                            data-testid={`adrns-nav-${item.label.toLowerCase().split(' ')[0]}`}
                            className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-emerald-600/20 text-emerald-200 border border-emerald-500/30' : 'text-emerald-100/60 hover:text-emerald-100 hover:bg-white/5'}`}>
                            <item.icon size={16} strokeWidth={1.6} /> {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-emerald-900/60">
                    <Link to="/app/dashboard" data-testid="adrns-back-btn"
                        className="flex items-center gap-2 text-sm text-emerald-100/60 hover:text-emerald-100 transition-colors">
                        <ArrowLeft size={15} /> Retour à mon espace
                    </Link>
                </div>
            </aside>
            <main className="flex-1 overflow-y-auto bg-background rounded-l-3xl my-2 mr-2 border border-border">
                <div className="p-8 max-w-6xl mx-auto">
                    <Outlet context={{ adrnsRole: status.role }} />
                </div>
            </main>
        </div>
    );
}
